import {ForecastMapExtent} from '../../api/types';
import {ExtentDraft} from './builderDraft';

/*
Viewport → custom-extent mapping for the map composer.

The backend's MapExtent schema 400s on anything outside the world
rectangle (lon ∈ [-180, 180], lat ∈ [-90, 90]) and cannot represent an
antimeridian-crossing extent (strictly min < max on both axes). MapLibre,
however, reports longitudes beyond ±180 on wrapped or low-zoom viewports,
so bounds are clamped into range first, rounded to 2 decimals AFTER
clamping, and rejected (null) — never "fixed up" into a guess — when the
clamped+rounded rectangle is reversed, degenerate, or narrower than the
minimum span. All rejection checks run on the ROUNDED values so any
extent this module returns is exactly the extent that validates and saves.
*/

// MapLibre getBounds() / onRegionDidChange order: [west, south, east, north].
export type ForecastMapViewportBounds = [number, number, number, number];

export const EXTENT_MIN_SPAN_DEGREES = 0.5;

const round2 = (value: number): number => Math.round(value * 100) / 100;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const viewportBoundsToExtent = (
  bounds: ForecastMapViewportBounds | null | undefined,
): ForecastMapExtent | null => {
  if (!bounds) {
    return null;
  }
  const west = round2(clamp(bounds[0], -180, 180));
  const south = round2(clamp(bounds[1], -90, 90));
  const east = round2(clamp(bounds[2], -180, 180));
  const north = round2(clamp(bounds[3], -90, 90));
  // NaN survives clamp/round; comparisons below would wave it through.
  if (![west, south, east, north].every(Number.isFinite)) {
    return null;
  }
  if (
    west >= east ||
    south >= north ||
    east - west < EXTENT_MIN_SPAN_DEGREES ||
    north - south < EXTENT_MIN_SPAN_DEGREES
  ) {
    return null;
  }
  return {min_lon: west, max_lon: east, min_lat: south, max_lat: north};
};

// Mirrors the backend MapExtent schema exactly: finite numbers inside the
// world rectangle, strictly min < max on both axes (no antimeridian
// crossing representable).
export const isValidExtent = (extent: ForecastMapExtent): boolean =>
  Number.isFinite(extent.min_lon) &&
  Number.isFinite(extent.max_lon) &&
  Number.isFinite(extent.min_lat) &&
  Number.isFinite(extent.max_lat) &&
  extent.min_lon >= -180 &&
  extent.max_lon <= 180 &&
  extent.min_lat >= -90 &&
  extent.max_lat <= 90 &&
  extent.min_lon < extent.max_lon &&
  extent.min_lat < extent.max_lat;

// String(n), matching builderDraft's numberToField (config → draft
// loading), so a viewport-picked extent shows up in the form exactly like
// a saved one and parses back via the draft's own parseNumber.
export const extentToDraftPatch = (extent: ForecastMapExtent): ExtentDraft => ({
  min_lon: String(extent.min_lon),
  max_lon: String(extent.max_lon),
  min_lat: String(extent.min_lat),
  max_lat: String(extent.max_lat),
});
